import type { SelectOption } from '@components';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Collapse, Divider, IconButton, TextField, useTheme } from '@mui/material';
import { FieldArray, type FieldArrayRenderProps, useFormikContext } from 'formik';
import { Add, Edit2, Trash } from 'iconsax-react';
import { type FC, type ReactElement, useContext, useState } from 'react';

import { DEFAULT_LOOKUP_LENGTH } from '@core/constants/common';
import { SlabContext } from '@core/context/SlabContext';
import { FormikCompany } from '@core/formik/company';
import {
  type CompanyContactType,
  EmptyFormikProject,
  type ProjectFormikType,
} from '@core/formik/project';
import Enums from '@core/types/api/Enums';
import type { Market } from '@core/types/api/Market/Market';
import { hasValue } from '@core/utils/equality';
import { ClearFormikField, SetFormikValue } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { NIL_UUID, randomUUID } from '@core/utils/uuid';
import { ExpandMore, ReferenceValueMatcher, MultiLookupInput } from '@web/components';
import { FieldWithCustomChip } from '@web/components/FieldWithCustomChip/FieldWithCustomChip';

import { AddressField } from '../../../../components/Address/AddressField';
import { ContactField } from '../../../../components/Contact/ContactField';
import { Input } from '../../../../components/Input/Input';
import { InputDropdown } from '../../../../components/InputDropdown/InputDropdown';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '../../../../components/LookupInput/ApiLookupInput';
import { SlabCard } from '../../../../components/SlabCard/SlabCard';
import { SlabCardBody } from '../../../../components/SlabCard/SlabCardBody';
import { SlabCardHeader } from '../../../../components/SlabCard/SlabCardHeader';
import {
  type SlabDragControl,
  SlabDraggableList,
  type SlabDropResult,
} from '../../../../components/SlabDraggableList/SlabDraggableList';
import { useSlabQuery } from '../../../../hooks/useSlabQuery';
import { enumLookups, lookups } from '../../../../utils/DomainHelpers';

const companyCategoryOpts = enumLookups(Enums.CompanyCategory);

/** Formik field holding every project company. Still named `contractors` because
 * mobile keeps the contractors/otherCompanies split; renaming it is part of merging both. */
const COMPANIES_FIELD = 'contractors';

interface ExistingCompanyFormProps {
  arrayHelpers: FieldArrayRenderProps;
  currentProjectCompany: CompanyContactType;
  index: number;
  dragControls: SlabDragControl;
}

const ExistingCompanyForm: FC<ExistingCompanyFormProps> = ({
  arrayHelpers,
  currentProjectCompany,
  index,
  dragControls,
}) => {
  const theme = useTheme();

  const ctx = useContext(SlabContext);
  const formikBag = useFormikContext<ProjectFormikType>();
  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);
  const currentCompanyID = currentProjectCompany.company.id;
  const {
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    data: selectedCompany,
  } = useSlabQuery(
    'GET company by ID',
    { pathParams: { id: currentCompanyID ?? '' } },
    { enabled: hasValue(currentCompanyID) },
  );

  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);

  const formikCompanies = formikBag.values[COMPANIES_FIELD];

  if (isLoadingCompany) {
    return <LoadingSpinner />;
  }

  if (isErrorCompany) {
    return <div>Failed to load companies</div>;
  }

  const companySalesperson =
    selectedCompany?.user !== undefined && selectedCompany?.user !== null
      ? selectedCompany.user.fullName()
      : '[not set]';

  return (
    <SlabCard>
      <SlabCardHeader sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <IconButton {...dragControls} sx={{ mt: 1 }}>
          <DragIndicatorIcon />
        </IconButton>

        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: 1, flexBasis: 160, display: 'flex' }}>
            <FieldWithCustomChip
              showChip={hasValue(selectedCompany)}
              label={selectedCompany?.category ?? ''}
              field={
                <ApiLookupInput
                  formState={formikBag.values}
                  name={`${COMPANIES_FIELD}.${index}.company`}
                  label='Company*'
                  disablePortal={false}
                  route={{
                    barrel: 'GET companies',
                    args: (inputText, { constructQueryParams }) =>
                      constructQueryParams(inputText, {
                        filterBy: [
                          {
                            operation: Enums.FilterOperation.Equals,
                            name: 'isArchived',
                            values: [false],
                          },
                        ],
                      }),
                    transform: (companyList): SelectOption<string>[] => {
                      const currentIDs = formikCompanies.map((mm) => mm.company.id ?? '');
                      const curSelectedCompanies = companyList.items.filter((c) =>
                        currentIDs.includes(c.id),
                      );
                      const filteredCompanies = companyList.items
                        .filter((c) => !currentIDs.includes(c.id))
                        .slice(0, DEFAULT_LOOKUP_LENGTH);

                      return lookups({
                        items: filteredCompanies,
                        label: (c) => c.name,
                        sublabels: (c) => c.lookupSublabels(usesDispatchCustomer),
                        // The count of selectable items excluding currently selected materials.
                        count: companyList.count - curSelectedCompanies.length,
                      });
                    },
                  }}
                  onMatchChange={(companyID): void => {
                    if (companyID === null) {
                      SetFormikValue(formikBag, `${COMPANIES_FIELD}.${index}.contact`, {
                        id: null,
                        option: null,
                      });
                    }

                    // Swapping the winner's row to another company drops the winner from the
                    // project's companies, which the API rejects (sc-13877).
                    const prevFormikCompanyID = currentProjectCompany.company.id;
                    const isWinnerReplaced =
                      hasValue(prevFormikCompanyID) &&
                      companyID !== prevFormikCompanyID &&
                      formikBag.values.winningCompany?.company?.id === prevFormikCompanyID;
                    if (isWinnerReplaced) {
                      ClearFormikField(formikBag, EmptyFormikProject, 'winningCompany');
                    }
                  }}
                />
              }
            />
          </Box>

          <Box sx={{ flex: 1, flexBasis: 160 }}>
            <TextField
              value={companySalesperson}
              label='Salesperson'
              disabled
              fullWidth
              InputLabelProps={{
                style: { color: theme.palette.SlabGray[800] },
              }}
            />
          </Box>
          {!currentProjectCompany.isNewContact && (
            <Box sx={{ flex: 1, flexBasis: 160 }}>
              <ApiLookupInput
                formState={formikBag.values}
                name={`${COMPANIES_FIELD}.${index}.contact`}
                label='Contact'
                route={{
                  barrel: 'GET contacts',
                  args: (inputText, { constructQueryParams }) =>
                    constructQueryParams(inputText, {
                      filterBy: [
                        {
                          operation: Enums.FilterOperation.Equals,
                          name: 'isArchived',
                          values: [false],
                        },
                        {
                          name: 'companyId',
                          operation: Enums.FilterOperation.Equals,
                          values: [currentProjectCompany.company.id ?? ''],
                        },
                      ],
                    }),
                  transform: (contactList): SelectOption<string>[] =>
                    lookups({ ...contactList, label: (c) => c.fullName() }),
                  options: { enabled: currentProjectCompany.company.id !== null },
                }}
                disabled={
                  !hasValue(currentProjectCompany.company.id) || currentProjectCompany.isNewContact
                }
              />

              <Button
                disabled={
                  !hasValue(currentProjectCompany.company.id) ||
                  currentProjectCompany.company.id === NIL_UUID
                }
                onClick={(): void => {
                  if (hasValue(currentProjectCompany.company.id)) {
                    SetFormikValue(
                      formikBag,
                      `${COMPANIES_FIELD}.${index}.contact`,
                      {
                        company: {
                          id: currentProjectCompany.company.id,
                          option: {
                            label: '',
                            value: currentProjectCompany.company.id,
                          },
                        },
                        isArchived: false,
                        emailAddress: '',
                        firstName: '',
                        id: NIL_UUID,
                        jobTitle: '',
                        lastName: '',
                        phoneNumber: '',
                        notes: '',
                      },
                      true,
                    );
                    SetFormikValue(
                      formikBag,
                      `${COMPANIES_FIELD}.${index}.isNewContact`,
                      true,
                      true,
                    );
                  }
                }}
                sx={{ textTransform: 'initial' }}
                startIcon={<Add />}
              >
                Add a contact
              </Button>
            </Box>
          )}
        </Box>

        <IconButton
          sx={{ mt: 1 }}
          onClick={(): void => {
            const maybeCurrentCompanyID = formikBag.getFieldMeta(
              `${COMPANIES_FIELD}.${index}.company.id`,
            ).value as string | null | undefined;
            const currentWinnerID = formikBag.getFieldMeta('winningCompany.company.id').value as
              | string
              | null
              | undefined;
            arrayHelpers.remove(index);

            // If current selected company was current winner, clear that value
            if (maybeCurrentCompanyID === currentWinnerID) {
              ClearFormikField(formikBag, EmptyFormikProject, 'winningCompany');
            }
          }}
        >
          <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
        </IconButton>
      </SlabCardHeader>

      <SlabCardBody>
        {currentProjectCompany.isNewContact && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <ContactField
              requiresContactInfo={requiresContactInfo}
              requiresContactEmail={requiresContactEmail}
              name={`${COMPANIES_FIELD}.${index}.contact`}
              fieldPrefix=''
            />

            <IconButton
              sx={{ mt: 1 }}
              onClick={() => {
                SetFormikValue(formikBag, `${COMPANIES_FIELD}.${index}.isNewContact`, false);
                SetFormikValue(formikBag, `${COMPANIES_FIELD}.${index}.contact`, {
                  id: null,
                  option: null,
                });
              }}
            >
              <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
            </IconButton>
          </Box>
        )}
      </SlabCardBody>
    </SlabCard>
  );
};

interface NewCompanyFormProps {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
  dragControls: SlabDragControl;
  marketOpts: SelectOption<string>[];
}

const NewCompanyForm: FC<NewCompanyFormProps> = ({
  arrayHelpers,
  index,
  dragControls,
  marketOpts,
}) => {
  const theme = useTheme();

  const formikBag = useFormikContext<ProjectFormikType>();

  const ctx = useContext(SlabContext);
  const areMarketsRequired = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresCompanyMarkets,
  ]);

  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);

  const [isOpen, setIsOpen] = useState<boolean>(true);

  return (
    <SlabCard>
      <SlabCardHeader sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <IconButton sx={{ mt: 1.25 }} {...dragControls}>
          <DragIndicatorIcon />
        </IconButton>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <Input name={`${COMPANIES_FIELD}.${index}.company.name`} label='Company name*' />
            <Input name={`${COMPANIES_FIELD}.${index}.company.alternateID`} label='ID' />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <InputDropdown
              sx={{ flex: 1 }}
              label='Company category'
              name={`${COMPANIES_FIELD}.${index}.company.category`}
              options={companyCategoryOpts}
            />
            {marketOpts.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <MultiLookupInput
                  name={`${COMPANIES_FIELD}.${index}.company.markets`}
                  label={requiredLabel('Markets', areMarketsRequired)}
                  withCheckbox
                  tip='Select specific markets otherwise the company will be associated with all markets.'
                  options={marketOpts}
                  onMatchChange={(matches): void => {
                    SetFormikValue(
                      formikBag,
                      `${COMPANIES_FIELD}.${index}.company.markets`,
                      matches.map(({ value, label }) => ({
                        id: value,
                        option: {
                          value,
                          label,
                        },
                      })),
                    );
                  }}
                  formValueMatcher={ReferenceValueMatcher}
                />
              </Box>
            )}
          </Box>
        </Box>
        <IconButton sx={{ mt: 1.25 }} onClick={(): void => arrayHelpers.remove(index)}>
          <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
        </IconButton>
        <ExpandMore sx={{ mt: 1.25 }} expand={isOpen} onClick={() => setIsOpen(!isOpen)} />
      </SlabCardHeader>

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <SlabCardBody>
          <ContactField
            requiresContactInfo={requiresContactInfo}
            requiresContactEmail={requiresContactEmail}
            name={`${COMPANIES_FIELD}.${index}`}
            fieldPrefix='contact'
          />
          <Divider />
          <AddressField name={`${COMPANIES_FIELD}.${index}.company.address`} />
        </SlabCardBody>
      </Collapse>
    </SlabCard>
  );
};

export const DynamicCompanyLookup = (): ReactElement => {
  const theme = useTheme();

  const formikBag = useFormikContext<ProjectFormikType>();

  const {
    isLoading: isLoadingCompanyStats,
    isError: isErrorCompanyStats,
    data: companyStats,
  } = useSlabQuery('GET company category aggregations', {});

  const { data: marketList } = useSlabQuery('GET markets', {});
  const marketOpts: SelectOption<string>[] =
    marketList === undefined
      ? []
      : lookups({
          ...marketList,
          label: (m: Market) => m.name ?? '',
        });

  if (isLoadingCompanyStats) {
    return <LoadingSpinner />;
  }

  if (isErrorCompanyStats || companyStats === undefined) {
    return <div>An error occurred</div>;
  }

  const totalCompanyCount = companyStats.reduce((total, ccAgg) => ccAgg.count + total, 0);

  const formikCompanies = formikBag.values[COMPANIES_FIELD];

  const onDragEnd = (result: SlabDropResult<CompanyContactType>): void => {
    SetFormikValue(formikBag, COMPANIES_FIELD, result.reordered);
  };

  return (
    <FieldArray
      name={COMPANIES_FIELD}
      render={(arrayHelpers): ReactElement => (
        <>
          <SlabDraggableList
            direction='vertical'
            droppableId='company-draggable'
            onReorder={onDragEnd}
            data={formikCompanies}
            renderItem={({ item, index, dragControls }) =>
              item.isNewCompany ? (
                <NewCompanyForm
                  arrayHelpers={arrayHelpers}
                  index={index}
                  dragControls={dragControls}
                  marketOpts={marketOpts}
                />
              ) : (
                <ExistingCompanyForm
                  arrayHelpers={arrayHelpers}
                  currentProjectCompany={item}
                  index={index}
                  dragControls={dragControls}
                />
              )
            }
            keyExtractor={(item, idx) => `${item.id}-${idx}`}
          />

          <Button
            type='button'
            disabled={formikCompanies.length >= totalCompanyCount}
            onClick={(): void =>
              arrayHelpers.push({
                id: randomUUID(),
                isNewCompany: false,
                isNewContact: false,
                company: { id: null, option: null },
                contact: { id: null, option: null },
              })
            }
            sx={{
              textTransform: 'none',
              color: theme.palette.SlabIndigo['700'],
            }}
            startIcon={<Add color={theme.palette.SlabIndigo['700']} />}
          >
            Add existing company
          </Button>
          <Button
            type='button'
            onClick={(): void =>
              arrayHelpers.push({
                isNewCompany: true,
                isNewContact: true,
                id: randomUUID(),
                company: {
                  ...FormikCompany(undefined),
                  category: Enums.CompanyCategory.Contractor,
                  markets: [],
                  dispatchCustomers: [],
                  user: null,
                },
                contact: {
                  id: NIL_UUID,
                  firstName: '',
                  lastName: '',
                  jobTitle: '',
                  emailAddress: '',
                  phoneNumber: '',
                },
              })
            }
            sx={{
              textTransform: 'none',
              color: theme.palette.SlabIndigo['700'],
            }}
            startIcon={<Edit2 color={theme.palette.SlabIndigo['700']} />}
          >
            Create new company
          </Button>
        </>
      )}
    />
  );
};
